import { LitElement, html, css } from 'lit';
import { classMap } from 'lit/directives/class-map.js';

import { LIT_CSS_VARIABLES } from '@Root/lib/lit-styles.js';
import { SHARED_ELEMENTS, SHARED_CLASSES } from '../../styles';
import {
  handleFileDownloadEvent,
  handleAdobeNavEvent,
} from '../../../../helpers/adobe-analytics.js';

class CtaText extends LitElement {
  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      css`
        a {
          display: inline-flex;
          font-size: 0.875rem;
          letter-spacing: -0.21px;
          line-height: 1.25em;
          white-space: nowrap;
          align-items: baseline;
        }

        h3 {
          text-wrap: balance;
          font-size: calc(11em / var(--default-rem-px-count));

          .right-arrow {
            padding-left: calc(8em / var(--default-rem-px-count));
            height: calc(9em / var(--default-rem-px-count));
          }
        }

        a.large-font {
          align-items: center;

          h3 {
            font-size: calc(16em / var(--default-rem-px-count));
            line-height: 166.667%;
            letter-spacing: -0.21px;
          }

          .right-arrow {
            padding-left: calc(16em / var(--default-rem-px-count));
            height: calc(10em / var(--default-rem-px-count));
          }
        }

        .download-icon {
          padding-right: calc(10em / var(--default-rem-px-count));
        }

        .right-arrow {
          padding-left: calc(8em / var(--default-rem-px-count));
          height: calc(9em / var(--default-rem-px-count));
        }

        .white-font {
          color: #ffffff;
        }

        @media (min-width: 600px) {
          h3 {
            font-size: calc(14rem / var(--default-rem-px-count));
            text-wrap: pretty;

            .right-arrow {
              padding-left: calc(8em / var(--default-rem-px-count));
              height: inherit;
            }
          }

          a.large-font {
            h3 {
              font-size: calc(18em / var(--default-rem-px-count));
              letter-spacing: -0.21px;
            }

            .right-arrow {
              padding-left: calc(16em / var(--default-rem-px-count));
              height: calc(12em / var(--default-rem-px-count));
            }
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      hidden: { type: Boolean },
      disabled: { type: Boolean },
      ctaData: { type: Object },
      ctaAnalyticsData: { type: Object },
    };
  }

  constructor() {
    super();
    this.ctaAnalyticsData = {};
  }

  render() {
    return !!this.parsedCtaData
      ? html`
          <a
            href=${this.urlString}
            target=${this.isTargetBlank}
            class="${classMap({
              'print-none': !this.reserveSpaceWhenHidden,
              'print-hidden': this.reserveSpaceWhenHidden,
              'white-font': this.useWhiteColors,
              'large-font': this.largeFontSize,
            })}"
            @click="${this.handleClick}"
          >
            ${this.leftDownloadSvg
              ? html`
                  <span class="download-icon print-none">
                    <img src="/static/icons/SVG/download-10x10.svg" />
                  </span>
                `
              : html``}
            <h3
              class="${classMap({
                bold: !this.normalFontWeight,
              })}"
            >
              ${this.textString}
            </h3>
            ${this.renderRightArrowSVG
              ? html`
                  <img class="right-arrow print-none" src=${this.svgSrc} />
                `
              : html``}
          </a>
        `
      : html``;
  }

  get parsedCtaData() {
    if (this.ctaData) return this.ctaData;

    this.ctaData = this.getAttribute('cta-data')
      ? JSON.parse(this.getAttribute('cta-data'))
      : null;

    return this.ctaData;
  }

  handleClick(event) {
    if (this.parsedCtaAnalyticsData) {
      this.sendAnalyticsDownloadEvent(this.ctaAnalyticsData, event);
    }
  }

  get parsedCtaAnalyticsData() {
    if (Object.keys(this.ctaAnalyticsData).length) return this.ctaAnalyticsData;

    this.ctaAnalyticsData = this.getAttribute('cta-analytics-data')
      ? JSON.parse(this.getAttribute('cta-analytics-data'))
      : {};

    this.ctaAnalyticsData.originalObject = this.parsedCtaData;
    return this.ctaAnalyticsData;
  }

  get renderRightArrowSVG() {
    return (
      !JSON.parse(this.getAttribute('hide-right-arrow-svg')) &&
      !!this.textString
    );
  }

  get svgSrc() {
    return `/static/icons/SVG/arrow-${this.colorString}-5x10.svg`;
  }

  get textString() {
    return this.ctaData.text || '';
  }

  get urlString() {
    return this.ctaData.url || '';
  }

  get isTargetBlank() {
    return !!JSON.parse(this.getAttribute('target-blank')) ? '_blank' : '_self';
  }

  get leftDownloadSvg() {
    return JSON.parse(this.getAttribute('left-download-svg')) || false;
  }

  get reserveSpaceWhenHidden() {
    return this.hasAttribute('reserve-space-when-hidden');
  }

  get normalFontWeight() {
    return this.ctaData.notBold;
  }

  get largeFontSize() {
    return this.ctaData.fontSize === 'large';
  }

  get colorString() {
    return this.useWhiteColors ? 'white' : 'black';
  }

  get useWhiteColors() {
    return this.ctaData.useWhite;
  }

  sendAnalyticsDownloadEvent(downloadsAnalyticsData, event) {
    // Google Analytics
    if (
      typeof gtag === 'function' &&
      Object.keys(downloadsAnalyticsData).length >= 0
    ) {
      gtag('event', 'custom_file_download', downloadsAnalyticsData);
    }

    // Adobe Analytics
    const ctaAnalyticsObj =
      typeof downloadsAnalyticsData === 'string'
        ? JSON.parse(downloadsAnalyticsData)
        : downloadsAnalyticsData;

    ctaAnalyticsObj.originalObject = this.ctaData;

    if (ctaAnalyticsObj.isDownload) {
      handleFileDownloadEvent(ctaAnalyticsObj);
    } else {
      ctaAnalyticsObj.event = event;
      handleAdobeNavEvent(ctaAnalyticsObj);
    }
  }
}
customElements.define('cta-text', CtaText);
